/**
 * Feature Flags Client Utilities
 *
 * These hooks read feature flags from the FeatureFlagsProvider context,
 * which is populated by the root loader. This is the recommended pattern
 * for accessing feature flags in React components.
 *
 * For server-side code, use ConfigService instead.
 */

import { AdminPrefs } from '#app/config'
import { useFeatureFlagsContext } from '#app/contexts/FeatureFlagsContext.tsx'

/**
 * Navigate to a value in a nested object by dot-separated path
 */
function getAtPath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.')
  let current: unknown = obj
  for (const p of parts) {
    if (!current || typeof current !== 'object' || !(p in current)) return undefined
    current = (current as Record<string, unknown>)[p]
  }
  return current
}

/**
 * Check if a feature is enabled (static, non-reactive)
 * 
 * @deprecated Prefer useFeatureFlag() hook in components
 * @param featurePath Path to feature (e.g., "resourceViewer.annotations.notes")
 * @returns boolean indicating if feature is enabled
 */
export function isFeatureEnabled(featurePath: string): boolean {
  const value = getAtPath(AdminPrefs.features as Record<string, unknown>, featurePath)
  return Boolean(value)
}

/**
 * Hook to check if a feature is enabled
 * 
 * Uses context from FeatureFlagsProvider (populated by root loader)
 * with fallback to static AdminPrefs.features
 * 
 * @param featurePath Path to feature (e.g., "course.studyPlan")
 * @returns boolean indicating if feature is enabled
 */
export function useFeatureFlag(featurePath: string): boolean {
  const ctx = useFeatureFlagsContext()
  const source = ctx ?? AdminPrefs.features
  const value = getAtPath(source as Record<string, unknown>, featurePath)
  return Boolean(value)
}

/**
 * Hook to get all feature flags
 * 
 * Uses context from FeatureFlagsProvider (populated by root loader)
 * with fallback to static AdminPrefs.features
 * 
 * @returns Object with all feature flags
 */
export function useFeatureFlags(): Record<string, unknown> {
  const ctx = useFeatureFlagsContext()
  return ctx ?? AdminPrefs.features ?? {}
}
